/**
 * Infinite scroll + filters (server-side, searchable dropdowns)
 *
 * - Filtering / sorting / paging all happen on Shopify's side: one small
 *   request per filter change or scroll page, instead of downloading the
 *   whole collection up front.
 * - Filter definitions come from Shopify Search & Discovery via the `filters`
 *   field, in the order they are arranged in the admin. Add or remove a filter
 *   there (e.g. Season) and the storefront follows with no code change.
 * - Every dropdown is a searchable combobox, laid out as a single toolbar row.
 */

/* ---------------------------------------------------------------
 * PER-STORE SETTINGS — the only block that differs between stores.
 *
 * sanaullastore.com      (sanaullastore01)        PKR, Yango installed
 *   STOREFRONT_TOKEN   = 'a98f2f84b830b892cc291968318a91cf'
 *   MONEY_PREFIX       = 'Rs. '
 *   MONEY_DECIMALS     = 0
 *   ENABLE_YANGO_BADGE = true
 *
 * go.sanaullastore.com   (sanaulla-international)  USD, no Yango
 *   STOREFRONT_TOKEN   = '50489c943f6457f7f3c9b542cf72b48e'
 *   MONEY_PREFIX       = 'USD. '
 *   MONEY_DECIMALS     = 2
 *   ENABLE_YANGO_BADGE = false
 * --------------------------------------------------------------- */
const STOREFRONT_TOKEN = '50489c943f6457f7f3c9b542cf72b48e';
const MONEY_PREFIX = 'USD. ';
const MONEY_DECIMALS = 2;
const ENABLE_YANGO_BADGE = false;

// Same-origin endpoint: avoids the CORS preflight the *.myshopify.com URL
// needed, and automatically targets whichever store the theme is installed on.
const API_URL = '/api/2024-10/graphql.json';

const FIRST_PAGE_SIZE = 12;
const PAGE_SIZE = 16;
const ACCENT = '#ed2590';
// Keep in sync with the stacked-layout media query in injectStyles().
const MOBILE_BREAKPOINT = 608;
// Show the search box only when a list is long enough to need it.
const SEARCHABLE_THRESHOLD = 8;

const SORT_OPTIONS = [
  { value: '',             label: 'Recommended' },
  { value: 'best-selling', label: 'Best Selling' },
  { value: 'newest',       label: 'Newest' },
  { value: 'price-asc',    label: 'Price: Low to High' },
  { value: 'price-desc',   label: 'Price: High to Low' },
  { value: 'az',           label: 'A to Z' },
  { value: 'za',           label: 'Z to A' },
];

const SORT_MAP = {
  '':             { sortKey: 'COLLECTION_DEFAULT', reverse: false },
  'best-selling': { sortKey: 'BEST_SELLING',       reverse: false },
  'newest':       { sortKey: 'CREATED',            reverse: true  },
  'price-asc':    { sortKey: 'PRICE',              reverse: false },
  'price-desc':   { sortKey: 'PRICE',              reverse: true  },
  'az':           { sortKey: 'TITLE',              reverse: false },
  'za':           { sortKey: 'TITLE',              reverse: true  },
};

const PRODUCTS_QUERY = `
  query Products(
    $handle: String!, $first: Int!, $after: String,
    $sortKey: ProductCollectionSortKeys, $reverse: Boolean,
    $filters: [ProductFilter!], $withFilters: Boolean!
  ) {
    collection(handle: $handle) {
      products(first: $first, after: $after, sortKey: $sortKey, reverse: $reverse, filters: $filters) {
        pageInfo { hasNextPage endCursor }
        nodes {
          id title handle
          featuredImage { url altText }
          variants(first: 1) { nodes { price { amount } compareAtPrice { amount } } }
        }
        filters @include(if: $withFilters) {
          id label type
          values { id label count input }
        }
      }
    }
  }`;

/* ---------- helpers ---------- */

function loadNoUiSliderAssets() {
  if (window.__noUiSliderPromise) return window.__noUiSliderPromise;
  window.__noUiSliderPromise = new Promise((resolve) => {
    if (typeof noUiSlider !== 'undefined') return resolve();
    const css = document.createElement('link');
    css.rel = 'stylesheet';
    css.href = 'https://cdn.jsdelivr.net/npm/nouislider@15.7.1/dist/nouislider.min.css';
    document.head.appendChild(css);
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/nouislider@15.7.1/dist/nouislider.min.js';
    script.onload = resolve;
    script.onerror = resolve; // keep going without the slider if the CDN fails
    document.body.appendChild(script);
  });
  return window.__noUiSliderPromise;
}

const esc = (v) =>
  String(v ?? '').replace(/&/g, '&amp;').replace(/"/g, '&quot;')
    .replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Thousands separators are applied to the integer part only, so decimals are
// never broken up (1234.5 -> "1,234.50", not "1,2,34.50").
function formatMoney(n, decimals = MONEY_DECIMALS) {
  const v = Number(n) || 0;
  const fixed = decimals > 0 ? v.toFixed(decimals) : String(Math.round(v));
  const parts = fixed.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
}

// noUiSlider needs a step suited to the range: 100 is fine for tens of
// thousands of rupees but useless across a few hundred dollars.
function niceStep(span) {
  const raw = span / 400;
  if (!isFinite(raw) || raw <= 1) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const n = raw / mag;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * mag;
}

// Fold accents/case so "Aqua Blue" matches "aqua" and "Emerald" matches "émerald".
const normalize = (s) =>
  String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

/* ---------- toolbar styles ---------- */

function injectStyles() {
  if (document.getElementById('sf-filter-styles')) return;
  const style = document.createElement('style');
  style.id = 'sf-filter-styles';
  style.textContent = `
  #filter-form.sf-bar{
    display:flex; align-items:flex-end; flex-wrap:nowrap;
    gap:6px; width:100%; margin:0 0 18px; padding:0;
  }
  .sf-field{flex:1 1 0; min-width:0; display:flex; flex-direction:column; gap:4px;}
  .sf-field__label{
    font-size:9px; letter-spacing:.05em; text-transform:uppercase;
    color:#8a8a8a; font-weight:600; line-height:1; white-space:nowrap;
    overflow:hidden; text-overflow:ellipsis;
  }

  /* --- combobox --- */
  .sf-dd{position:relative;}
  .sf-dd__btn{
    width:100%; display:flex; align-items:center; justify-content:space-between;
    gap:5px; padding:9px 10px; border:1px solid #dcdcdc; border-radius:8px;
    background:#fff; font-size:13px; line-height:1.2; color:#222;
    cursor:pointer; text-align:left; font-family:inherit; transition:border-color .15s,box-shadow .15s;
  }
  .sf-dd__btn:hover{border-color:#b0b0b0;}
  .sf-dd__btn:focus-visible{outline:none;border-color:${ACCENT};box-shadow:0 0 0 3px rgba(237,37,144,.15);}
  .sf-dd.is-active .sf-dd__btn{border-color:${ACCENT};color:${ACCENT};font-weight:600;}
  .sf-dd.is-open .sf-dd__btn{border-color:${ACCENT};box-shadow:0 0 0 3px rgba(237,37,144,.12);}
  .sf-dd__value{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1 1 auto;}
  .sf-dd__caret{flex:0 0 auto;width:10px;height:10px;opacity:.55;transition:transform .15s;}
  .sf-dd.is-open .sf-dd__caret{transform:rotate(180deg);}

  .sf-dd__panel{
    position:absolute; z-index:120; top:calc(100% + 6px); left:0;
    width:clamp(220px,100%,300px); background:#fff; border:1px solid #e4e4e4;
    border-radius:10px; box-shadow:0 12px 32px rgba(0,0,0,.14); padding:8px;
  }
  .sf-dd__panel--right{left:auto;right:0;}
  .sf-dd__search{
    width:100%; padding:8px 10px; border:1px solid #e2e2e2; border-radius:6px;
    font-size:13px; font-family:inherit; margin-bottom:6px; box-sizing:border-box;
  }
  .sf-dd__search:focus{outline:none;border-color:${ACCENT};}
  .sf-dd__list{list-style:none;margin:0;padding:0;max-height:258px;overflow-y:auto;overscroll-behavior:contain;}
  .sf-dd__opt{
    display:flex; align-items:center; justify-content:space-between; gap:8px;
    padding:8px 10px; border-radius:6px; font-size:13px; color:#333; cursor:pointer;
  }
  .sf-dd__opt:hover,.sf-dd__opt.is-cursor{background:#f5f5f5;}
  .sf-dd__opt[aria-selected="true"]{color:${ACCENT};font-weight:600;}
  .sf-dd__opt-label{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
  .sf-dd__count{flex:0 0 auto;font-size:11px;color:#a0a0a0;}
  .sf-dd__empty{padding:12px 10px;font-size:12px;color:#999;text-align:center;}

  /* --- price --- */
  .sf-field--price{flex:1.5 1 0;}
  .sf-price__box{padding:6px 10px 0;border:1px solid #dcdcdc;border-radius:8px;background:#fff;height:40px;box-sizing:border-box;}
  .sf-price__values{display:flex;justify-content:space-between;font-size:11px;color:#555;margin-bottom:7px;}
  .sf-price__values strong{color:#222;font-weight:600;}
  #price-slider{height:4px;margin:0 5px;border:none;box-shadow:none;background:#e6e6e6;}
  #price-slider .noUi-connect{background:${ACCENT};}
  #price-slider .noUi-handle{
    background:#fff;border:2px solid ${ACCENT};box-shadow:0 1px 4px rgba(0,0,0,.2);
    width:14px;height:14px;border-radius:50%;right:-7px;top:-6px;cursor:grab;
  }
  #price-slider .noUi-handle:before,#price-slider .noUi-handle:after{display:none;}

  /* --- reset --- */
  .sf-field--reset{flex:0 0 auto;}
  .sf-reset{
    padding:9px 14px;border:none;border-radius:8px;background:${ACCENT};color:#fff;
    font-size:13px;font-weight:600;font-family:inherit;cursor:pointer;white-space:nowrap;
    transition:opacity .15s;
  }
  .sf-reset:hover{opacity:.88;}

  /* --- responsive --- */
  @media (max-width:1023px){
    #filter-form.sf-bar{flex-wrap:wrap;gap:10px;}
    .sf-field{flex:1 1 160px;}
    .sf-field--price{flex:1 1 220px;}
  }
  @media (max-width:608px){
    #filter-form.sf-bar{flex-direction:column;align-items:stretch;}
    .sf-field,.sf-field--price,.sf-field--reset{flex:1 1 auto;width:100%;}
    .sf-dd__panel{width:100%;}
    .sf-reset{width:100%;}
  }

  /* --- loading placeholders --- */
  .sf-skeleton{flex:1 1 0;height:40px;border-radius:8px;background:linear-gradient(90deg,#f0f0f0 25%,#e6e6e6 37%,#f0f0f0 63%);background-size:400% 100%;animation:sf-shimmer 1.3s ease infinite;}
  @keyframes sf-shimmer{0%{background-position:100% 50%}100%{background-position:0 50%}}

  /* --- bottom loader --- */
  .sf-loader{display:flex;align-items:center;justify-content:center;gap:9px;width:100%;padding:26px 0;}
  .sf-loader[hidden]{display:none;}
  .sf-loader__ring{
    width:20px;height:20px;border-radius:50%;
    border:2px solid #e6e6e6;border-top-color:${ACCENT};
    animation:sf-spin .7s linear infinite;
  }
  .sf-loader__text{font-size:12px;color:#9a9a9a;letter-spacing:.02em;}
  @keyframes sf-spin{to{transform:rotate(360deg)}}
  @media (prefers-reduced-motion:reduce){
    .sf-loader__ring{animation-duration:2.4s;}
  }
  .sf-end{width:100%;text-align:center;font-size:12px;color:#aaa;padding:26px 0 6px;}
  .sf-end[hidden]{display:none;}
  `;
  document.head.appendChild(style);
}

/* ---------- searchable dropdown ---------- */

/**
 * options: [{ value, label, count }] — `value` '' means "no selection".
 * Returns { el, getValue, setValue, reset }.
 */
function createDropdown({ label, name, options, value = '', onChange }) {
  let current = value;
  let cursor = -1;
  let shown = options.slice();

  const root = document.createElement('div');
  root.className = 'sf-dd';

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'sf-dd__btn';
  btn.setAttribute('aria-haspopup', 'listbox');
  btn.setAttribute('aria-expanded', 'false');

  const valueEl = document.createElement('span');
  valueEl.className = 'sf-dd__value';

  const caret = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  caret.setAttribute('class', 'sf-dd__caret');
  caret.setAttribute('viewBox', '0 0 12 12');
  caret.setAttribute('aria-hidden', 'true');
  const caretPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  caretPath.setAttribute('d', 'M2 4.5 6 8.5 10 4.5');
  caretPath.setAttribute('fill', 'none');
  caretPath.setAttribute('stroke', 'currentColor');
  caretPath.setAttribute('stroke-width', '1.6');
  caretPath.setAttribute('stroke-linecap', 'round');
  caretPath.setAttribute('stroke-linejoin', 'round');
  caret.appendChild(caretPath);

  btn.appendChild(valueEl);
  btn.appendChild(caret);

  const panel = document.createElement('div');
  panel.className = 'sf-dd__panel';
  panel.hidden = true;

  const searchable = options.length > SEARCHABLE_THRESHOLD;
  const search = document.createElement('input');
  if (searchable) {
    search.type = 'text';
    search.className = 'sf-dd__search';
    search.placeholder = `Search ${(name || label).toLowerCase()}…`;
    search.setAttribute('aria-label', `Search ${name || label}`);
    panel.appendChild(search);
  }

  const list = document.createElement('ul');
  list.className = 'sf-dd__list';
  list.setAttribute('role', 'listbox');
  panel.appendChild(list);

  root.appendChild(btn);
  root.appendChild(panel);

  function syncButton() {
    const sel = options.find((o) => o.value === current);
    valueEl.textContent = sel && sel.value ? sel.label : label;
    root.classList.toggle('is-active', !!current);
  }

  function renderList(query) {
    const q = normalize(query);
    // While searching, hide the "clear" row so every result is a real value.
    shown = q
      ? options.filter((o) => o.value && normalize(o.label).includes(q))
      : options.slice();

    list.innerHTML = '';
    if (!shown.length) {
      const empty = document.createElement('li');
      empty.className = 'sf-dd__empty';
      empty.textContent = 'No matches';
      list.appendChild(empty);
      cursor = -1;
      return;
    }

    const frag = document.createDocumentFragment();
    shown.forEach((opt, i) => {
      const li = document.createElement('li');
      li.className = 'sf-dd__opt' + (i === cursor ? ' is-cursor' : '');
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', String(opt.value === current));
      li.dataset.value = opt.value;

      const lab = document.createElement('span');
      lab.className = 'sf-dd__opt-label';
      lab.textContent = opt.label;
      li.appendChild(lab);

      if (typeof opt.count === 'number') {
        const c = document.createElement('span');
        c.className = 'sf-dd__count';
        c.textContent = opt.count;
        li.appendChild(c);
      }

      li.addEventListener('mousedown', (e) => {
        e.preventDefault(); // keep focus so blur doesn't close before we select
        select(opt.value);
      });
      frag.appendChild(li);
    });
    list.appendChild(frag);
  }

  function moveCursor(delta) {
    if (!shown.length) return;
    cursor = (cursor + delta + shown.length) % shown.length;
    [...list.children].forEach((li, i) => li.classList.toggle('is-cursor', i === cursor));
    const active = list.children[cursor];
    if (active && active.scrollIntoView) active.scrollIntoView({ block: 'nearest' });
  }

  function open() {
    if (!panel.hidden) return;
    closeAllDropdowns(root);
    panel.hidden = false;
    root.classList.add('is-open');
    btn.setAttribute('aria-expanded', 'true');

    // Flip the panel if it would run off the right edge.
    panel.classList.remove('sf-dd__panel--right');
    const rect = panel.getBoundingClientRect();
    if (rect.right > window.innerWidth - 8) panel.classList.add('sf-dd__panel--right');

    cursor = shown.findIndex((o) => o.value === current);
    if (searchable) { search.value = ''; }
    renderList('');
    if (searchable) search.focus();
  }

  function close() {
    if (panel.hidden) return;
    panel.hidden = true;
    root.classList.remove('is-open');
    btn.setAttribute('aria-expanded', 'false');
  }

  function select(v) {
    close();
    if (v === current) return;
    current = v;
    syncButton();
    if (onChange) onChange(v);
  }

  btn.addEventListener('click', () => (panel.hidden ? open() : close()));

  if (searchable) {
    search.addEventListener('input', () => { cursor = -1; renderList(search.value); });
  }

  root.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { close(); btn.focus(); return; }
    if (panel.hidden) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
      return;
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); moveCursor(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); moveCursor(-1); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (cursor >= 0 && shown[cursor]) select(shown[cursor].value);
    } else if (e.key === 'Tab') close();
  });

  root.__closeDropdown = close;
  syncButton();
  renderList('');

  return {
    el: root,
    getValue: () => current,
    setValue: (v) => { current = v; syncButton(); },
    reset: () => { current = ''; syncButton(); },
  };
}

const openRoots = new Set();
function closeAllDropdowns(except) {
  openRoots.forEach((r) => { if (r !== except && r.__closeDropdown) r.__closeDropdown(); });
}
document.addEventListener('click', (e) => {
  openRoots.forEach((r) => { if (!r.contains(e.target) && r.__closeDropdown) r.__closeDropdown(); });
});

/* ---------- main ---------- */

document.addEventListener('DOMContentLoaded', function () {
  const section = document.querySelector("[data-section-type='infinite-scroll-products']");
  if (!section) return;

  const productContainer = section.querySelector('.infinite-product-grid');
  const filterParent = document.querySelector('.custom-product-slider-section');
  const filterForm = section.querySelector('#filter-form');

  if (!productContainer) return console.error('Product grid not found!');
  if (!filterParent) return console.error('Section not found!');
  if (!filterForm) return console.error('Filter form not found!');

  injectStyles();
  filterForm.classList.add('sf-bar');

  const collectionHandle = section.dataset.collectionHandle || 'trending';

  // Start the slider library download in parallel; products never wait for it.
  const sliderReady = loadNoUiSliderAssets();

  /* ----- mobile filter toggle ----- */
  const filterBtn = document.createElement('button');
  filterBtn.type = 'button';
  filterBtn.innerHTML = ` <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
      <path d="M6 10.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5zm-2-3a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zm-2-3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5z"/>
    </svg> Filter`;
  filterBtn.className = 'filter-btn';
  filterBtn.style.display = 'none';
  filterParent.insertAdjacentElement('afterend', filterBtn);

  filterBtn.setAttribute('aria-expanded', 'false');
  filterBtn.setAttribute('aria-controls', 'filter-form');

  // Whether the shopper has opened the panel on a narrow screen. Kept in a
  // variable rather than read back off the element, so nothing else can
  // silently reset it.
  let mobileOpen = false;

  function applyMode(isMobile) {
    if (isMobile) {
      filterBtn.style.display = 'flex';
      filterForm.style.display = mobileOpen ? 'flex' : 'none';
    } else {
      filterBtn.style.display = 'none';
      filterForm.style.display = 'flex';
    }
    filterBtn.setAttribute('aria-expanded', String(isMobile && mobileOpen));
  }

  // On phones, scrolling shows and hides the browser's address bar, which
  // changes the viewport height and fires `resize`; opening the keyboard for a
  // search box does the same. The previous code re-ran the mobile branch on
  // every one of those and closed the panel mid-scroll. So only act when the
  // breakpoint is genuinely crossed, never on a height-only change.
  let isMobileLayout = null;

  function syncMode() {
    const nowMobile = window.innerWidth <= MOBILE_BREAKPOINT;
    if (nowMobile === isMobileLayout) return;
    isMobileLayout = nowMobile;
    applyMode(nowMobile);
  }

  syncMode();
  window.addEventListener('resize', syncMode);
  window.addEventListener('orientationchange', syncMode);

  filterBtn.addEventListener('click', () => {
    mobileOpen = !mobileOpen;
    applyMode(true);
  });

  /* ----- state ----- */
  const state = {
    sort: '',
    listFilters: {},   // filterId -> parsed ProductFilter input
    price: null,       // { min, max } or null when at full range
    cursor: null,
    hasNext: false,
    loading: false,
    requestId: 0,
    abort: null,
    totalRendered: 0,
    filtersRendered: false,
    priceBounds: null,
  };

  const dropdowns = [];   // every combobox, for Reset

  /* ----- API ----- */
  async function fetchPage({ first, after, withFilters, signal }) {
    const { sortKey, reverse } = SORT_MAP[state.sort] || SORT_MAP[''];
    const filters = Object.values(state.listFilters);
    if (state.price) filters.push({ price: state.price });

    const res = await fetch(API_URL, {
      method: 'POST',
      signal,
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
      },
      body: JSON.stringify({
        query: PRODUCTS_QUERY,
        variables: {
          handle: collectionHandle,
          first,
          after,
          sortKey,
          reverse,
          filters: filters.length ? filters : null,
          withFilters: !!withFilters,
        },
      }),
    });
    const json = await res.json();
    if (json.errors || !json.data || !json.data.collection) {
      throw new Error((json.errors && json.errors[0] && json.errors[0].message) || 'Collection not found');
    }
    return json.data.collection.products;
  }

  /* ----- rendering ----- */
  function renderSkeletons(count) {
    let html = '';
    for (let i = 0; i < count; i++) {
      html += `
        <div class="grid__item grid-product small--one-half medium-up--one-quarter skeleton">
          <div class="product-card">
            <div class="product-card__image-wrapper skeleton-image"></div>
            <div class="product-card__info">
              <div class="skeleton-text skeleton-title"></div>
              <div class="skeleton-text skeleton-price"></div>
            </div>
          </div>
        </div>`;
    }
    productContainer.insertAdjacentHTML('beforeend', html);
  }

  function renderFilterSkeletons() {
    filterForm.innerHTML =
      '<div class="sf-skeleton"></div><div class="sf-skeleton"></div><div class="sf-skeleton"></div>' +
      '<div class="sf-skeleton"></div><div class="sf-skeleton"></div><div class="sf-skeleton"></div>';
  }

  function renderYangoBadge(product) {
    const YANGO = {
      appId: '3958b727-f08d-50b5-acc1-6471871adb25',
      extensionType: 'CatalogCardBadgeBasic',
      extensionAsset: 'CatalogCardBadgeBasicExtension',
      adapterSlug: 'noop',
      colorTheme: 'light',
      payOrigin: 'https://pay.yango.com',
    };
    const BASE = `${YANGO.payOrigin}/shopify/extensions/assets/`;
    const BOOTSTRAP_URL = `${BASE}${YANGO.extensionAsset}/js/bootstrap?app_id=${YANGO.appId}`;
    const STYLES_URL = `${BASE}${YANGO.extensionAsset}/css/styles?app_id=${YANGO.appId}`;

    const variantId = product.variantId ?? product.id;
    const extensionId = `${YANGO.extensionType}-${variantId}`;
    // Yango expects the price in minor units (price * 100), as before.
    const currentPrice = Math.round(Number(product.price) * 100);

    return `
      <div style="margin-top: 5px; display: flex; justify-content: center; pointer-events: none;" onclick="return false">
        <div
          data-yango-extension-type="${YANGO.extensionType}"
          data-yango-extension-id="${esc(extensionId)}"
          data-yango-widget-rendered="false"
          data-yango-adapter="${YANGO.adapterSlug}"
          data-yango-app-id="${YANGO.appId}"
          data-yango-pay-origin="${YANGO.payOrigin}"
          data-yango-color-theme="${YANGO.colorTheme}"
          data-yango-current-price="${currentPrice}"
          data-yango-styles-href="${STYLES_URL}"
          data-yango-style-font-size="xs"
          data-yango-selected-variant="${esc(variantId)}"
        >
          <template shadowrootmode="open"><div class="yango-mount-container"></div></template>
        </div>
        <script
          data-yango-extension-type="${YANGO.extensionType}"
          data-yango-extension-id="${esc(extensionId)}"
          data-yango-extension-executed="false"
          type="module"
          async
          src="${BOOTSTRAP_URL}"
          onload="window[Symbol.for('YPL')].bootstrapExtensionAfterLoadingScript(this)"
        ><\/script>
      </div>`;
  }

  function activateYangoBadges(container) {
    container.querySelectorAll('script[data-yango-extension-type]').forEach((old) => {
      if (old.dataset.yangoActivated) return;
      const s = document.createElement('script');
      for (const a of old.attributes) s.setAttribute(a.name, a.value);
      s.dataset.yangoActivated = 'true';
      old.replaceWith(s);
    });
  }

  function renderProducts(nodes) {
    const html = nodes.map((node, index) => {
      const variant = node.variants.nodes[0];
      const price = variant ? parseFloat(variant.price.amount) : 0;
      const compareAt = variant && variant.compareAtPrice ? parseFloat(variant.compareAtPrice.amount) : null;
      const onSale = compareAt && compareAt > price;

      const currentIndex = state.totalRendered + index;
      const loadingAttr = currentIndex < 4 ? 'eager' : 'lazy';
      const fetchPriority = currentIndex < 4 ? 'fetchpriority="high"' : '';

      const imageUrl = node.featuredImage
        ? `${node.featuredImage.url}${node.featuredImage.url.includes('?') ? '&' : '?'}width=400`
        : 'https://via.placeholder.com/300x400?text=No+Image';

      const savingsPercent = onSale ? Math.round(((compareAt - price) / compareAt) * 100) : 0;

      return `
        <div class="grid__item grid-product small--one-half medium-up--one-quarter" data-product-id="${esc(node.id)}" data-product-handle="${esc(node.handle)}">
          <a href="/products/${esc(node.handle)}" class="product-card-link">
            <div class="product-card">
              <div class="product-card__image-wrapper">
                <img src="${esc(imageUrl)}" alt="${esc(node.title)}" loading="${loadingAttr}" ${fetchPriority}
                     width="300" height="400" decoding="async" />
              </div>
              <div class="grid-product__meta">
                <div class="grid-product__title grid-product__title--body">${esc(node.title)}</div>
                ${ENABLE_YANGO_BADGE ? renderYangoBadge({ id: node.id, price }) : ''}
                <div class="product-card__price product-card-price">
                  ${
                    onSale
                      ? `<span class="original-price">${MONEY_PREFIX}${formatMoney(compareAt)}</span>
                         <span class="sale-price">${MONEY_PREFIX}${formatMoney(price)}</span>
                         <span class="grid-product__price--savings"><span class="price--savings">Save ${savingsPercent}%</span></span>`
                      : `<span class="regular-price">${MONEY_PREFIX}${formatMoney(price)}</span>`
                  }
                </div>
              </div>
            </div>
          </a>
        </div>`;
    });

    const before = productContainer.children.length;
    state.totalRendered += nodes.length;
    productContainer.insertAdjacentHTML('beforeend', html.join(''));

    // Each badge boots its own module script, so activate a card's badge only
    // once that card is close to the viewport instead of all at once.
    if (ENABLE_YANGO_BADGE) {
      const added = Array.prototype.slice.call(productContainer.children, before);
      added.forEach((card) => yangoObserver.observe(card));
    }
  }

  const yangoObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        yangoObserver.unobserve(entry.target);
        activateYangoBadges(entry.target);
      });
    },
    { rootMargin: '200px 0px' }
  );

  function renderEmpty() {
    productContainer.innerHTML =
      "<div class='filter-prod-nf'>" +
      "<img src='//cdn.shopify.com/s/files/1/0653/0842/6330/files/search-bar.png?v=1748428120' width='130px' loading='lazy'>" +
      "<p>Oops! We couldn't find anything. </p>" +
      "<p>No results for your search. Try another term to find what you're looking for!</p></div>";
  }

  function renderError() {
    productContainer.innerHTML =
      "<div class='filter-prod-nf'><p>Something went wrong while loading products.</p>" +
      "<p><button type='button' class='sf-reset' id='infinite-retry'>Try again</button></p></div>";
    const retry = document.getElementById('infinite-retry');
    if (retry) retry.addEventListener('click', () => resetAndLoad());
  }

  /* ----- loading ----- */

  // A filter change replaces the grid with a short one. If the shopper had
  // scrolled past the top of the grid they would land mid-list, so bring the
  // first row of results back into view.
  function scrollGridIntoView() {
    const top = productContainer.getBoundingClientRect().top + window.scrollY;
    if (window.scrollY > top - 120) {
      window.scrollTo({ top: Math.max(0, top - 120), behavior: 'smooth' });
    }
  }

  async function resetAndLoad({ withFilters = false } = {}) {
    if (state.abort) state.abort.abort();
    state.abort = new AbortController();
    const myRequest = ++state.requestId;

    state.cursor = null;
    state.hasNext = false;
    state.totalRendered = 0;
    state.loading = true;
    autoLoads = 0;
    setLoadingUI(false); // skeletons cover this phase, not the bottom spinner
    productContainer.innerHTML = '';
    renderSkeletons(FIRST_PAGE_SIZE);
    if (withFilters) renderFilterSkeletons();
    else scrollGridIntoView();

    try {
      const page = await fetchPage({
        first: FIRST_PAGE_SIZE,
        after: null,
        withFilters,
        signal: state.abort.signal,
      });
      if (myRequest !== state.requestId) return; // a newer request took over

      productContainer.innerHTML = '';
      state.cursor = page.pageInfo.endCursor;
      state.hasNext = page.pageInfo.hasNextPage;

      if (!page.nodes.length) renderEmpty();
      else renderProducts(page.nodes);

      if (withFilters && !state.filtersRendered) {
        state.filtersRendered = true;
        renderFilterBar(page.filters || []);
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Failed to load products:', err);
      if (myRequest === state.requestId) {
        productContainer.innerHTML = '';
        renderError();
      }
    } finally {
      if (myRequest === state.requestId) {
        state.loading = false;
        setLoadingUI(false);
        recheckSentinel();
      }
    }
  }

  async function loadMore() {
    if (state.loading || !state.hasNext) return;
    state.loading = true;
    setLoadingUI(true);
    const myRequest = state.requestId;
    const signal = state.abort ? state.abort.signal : undefined;

    try {
      const page = await fetchPage({ first: PAGE_SIZE, after: state.cursor, signal });
      if (myRequest !== state.requestId) return;
      state.cursor = page.pageInfo.endCursor;
      state.hasNext = page.pageInfo.hasNextPage;
      renderProducts(page.nodes);
    } catch (err) {
      if (err.name !== 'AbortError') console.error('Failed to load more products:', err);
    } finally {
      if (myRequest === state.requestId) {
        state.loading = false;
        setLoadingUI(false);
        recheckSentinel();
      }
    }
  }

  /* ----- filter bar ----- */

  function makeField(labelText, control, extraClass) {
    const field = document.createElement('div');
    field.className = 'sf-field' + (extraClass ? ' ' + extraClass : '');
    const label = document.createElement('span');
    label.className = 'sf-field__label';
    label.textContent = labelText;
    field.appendChild(label);
    field.appendChild(control);
    return field;
  }

  /**
   * Renders in the order Search & Discovery returns, which is the order the
   * filters are arranged in the Shopify admin. Add "Season" there and it
   * appears here automatically.
   */
  async function renderFilterBar(filters) {
    filterForm.innerHTML = '';
    dropdowns.length = 0;
    openRoots.clear();

    const priceFilter = filters.find((f) => f.type === 'PRICE_RANGE');
    let priceField = null;
    if (priceFilter && priceFilter.values[0]) {
      let bounds = null;
      try {
        const p = JSON.parse(priceFilter.values[0].input).price;
        bounds = { min: Math.floor(p.min), max: Math.ceil(p.max) };
      } catch (e) { /* ignore */ }
      if (bounds && bounds.max > bounds.min) {
        state.priceBounds = bounds;
        priceField = buildPriceField(bounds);
      }
    }

    filters.forEach((f) => {
      if (f.type === 'PRICE_RANGE') {
        if (priceField) filterForm.appendChild(priceField);
        return;
      }
      if (f.type !== 'LIST') return;

      const options = [{ value: '', label: `All ${f.label}` }].concat(
        f.values
          .filter((v) => v.count > 0)
          .sort((a, b) => a.label.localeCompare(b.label))
          .map((v) => ({ value: v.input, label: v.label, count: v.count }))
      );
      if (options.length <= 1) return;

      const dd = createDropdown({
        // The field label sits above the control, so "All" alone reads clearly
        // and keeps every control narrow enough to share one row.
        label: 'All',
        name: f.label,
        options,
        onChange: (val) => {
          if (val) {
            try { state.listFilters[f.id] = JSON.parse(val); } catch (e) { return; }
          } else {
            delete state.listFilters[f.id];
          }
          resetAndLoad();
        },
      });
      openRoots.add(dd.el);
      dropdowns.push(dd);
      filterForm.appendChild(makeField(f.label, dd.el));
    });

    // Sort
    const sortDd = createDropdown({
      label: 'Sort By',
      options: [{ value: '', label: 'Sort By' }].concat(SORT_OPTIONS.filter((o) => o.value)),
      onChange: (val) => { state.sort = val; resetAndLoad(); },
    });
    openRoots.add(sortDd.el);
    dropdowns.push(sortDd);
    filterForm.appendChild(makeField('Sort', sortDd.el));

    // Reset
    const resetButton = document.createElement('button');
    resetButton.type = 'button';
    resetButton.className = 'sf-reset';
    resetButton.textContent = 'Reset Filters';
    resetButton.addEventListener('click', (e) => {
      e.preventDefault();
      state.listFilters = {};
      state.price = null;
      state.sort = '';
      dropdowns.forEach((d) => d.reset());
      const slider = document.querySelector('#price-slider');
      if (slider && slider.noUiSlider && state.priceBounds) {
        slider.noUiSlider.set([state.priceBounds.min, state.priceBounds.max]);
      }
      resetAndLoad();
    });
    const resetField = makeField(' ', resetButton, 'sf-field--reset');
    filterForm.appendChild(resetField);

    // The slider library may still be downloading; mount it when ready.
    if (priceField) {
      await sliderReady;
      mountPriceSlider(priceField);
    }
  }

  let pendingSlider = null;

  function buildPriceField(bounds) {
    const box = document.createElement('div');
    box.className = 'sf-price__box';

    const values = document.createElement('div');
    values.className = 'sf-price__values';
    values.innerHTML =
      `<span>Min: <strong class="price-min">${formatMoney(bounds.min, 0)}</strong></span>` +
      `<span>Max: <strong class="price-max">${formatMoney(bounds.max, 0)}</strong></span>`;

    const slider = document.createElement('div');
    slider.id = 'price-slider';

    box.appendChild(values);
    box.appendChild(slider);
    pendingSlider = { slider, values, bounds };
    return makeField('Price Range', box, 'sf-field--price');
  }

  function mountPriceSlider() {
    if (!pendingSlider || typeof noUiSlider === 'undefined') return;
    const { slider, values, bounds } = pendingSlider;
    pendingSlider = null;

    noUiSlider.create(slider, {
      start: [bounds.min, bounds.max],
      connect: true,
      range: { min: bounds.min, max: bounds.max },
      step: niceStep(bounds.max - bounds.min),
      format: { to: (v) => Math.round(v), from: (v) => Math.round(v) },
    });

    slider.noUiSlider.on('update', (vals) => {
      values.querySelector('.price-min').textContent = formatMoney(vals[0], 0);
      values.querySelector('.price-max').textContent = formatMoney(vals[1], 0);
    });

    slider.noUiSlider.on('change', (vals) => {
      const min = Number(vals[0]);
      const max = Number(vals[1]);
      state.price = min <= bounds.min && max >= bounds.max ? null : { min, max };
      resetAndLoad();
    });
  }

  /* ----- infinite scroll ----- */

  // Minimal spinner shown only while the next page is in flight.
  const loader = document.createElement('div');
  loader.className = 'sf-loader';
  loader.hidden = true;
  loader.innerHTML = '<span class="sf-loader__ring"></span>';
  loader.setAttribute('role', 'status');
  loader.setAttribute('aria-label', 'Loading more products');
  productContainer.insertAdjacentElement('afterend', loader);

  const endNote = document.createElement('div');
  endNote.className = 'sf-end';
  endNote.hidden = true;
  endNote.textContent = "That's everything in this selection.";
  loader.insertAdjacentElement('afterend', endNote);

  const sentinel = document.createElement('div');
  sentinel.className = 'infinite-scroll-sentinel';
  sentinel.style.cssText = 'width:100%;height:1px;';
  endNote.insertAdjacentElement('afterend', sentinel);

  function setLoadingUI(isLoading) {
    loader.hidden = !isLoading;
    endNote.hidden = isLoading || state.hasNext || state.totalRendered === 0;
  }

  // Each page makes the grid taller, but the sentinel can still sit inside the
  // observer's margin, which would chain requests until the collection runs
  // out. Allow a couple of automatic fills, then wait for a real scroll.
  let autoLoads = 0;
  window.addEventListener('scroll', () => { autoLoads = 0; }, { passive: true });

  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries[0].isIntersecting) return;
      if (autoLoads >= 2) return;
      autoLoads++;
      loadMore();
    },
    { rootMargin: '300px 0px' }
  );
  observer.observe(sentinel);

  function recheckSentinel() {
    observer.unobserve(sentinel);
    observer.observe(sentinel);
  }

  /* ----- quick view ----- */
  function getOrCreateModal(productId, handle) {
    let modal = document.querySelector(`#QuickShopModal-${productId}`);
    if (!modal) {
      const modalHTML = `
        <div id="QuickShopModal-${productId}" class="modal modal--square modal--quick-shop" data-product-id="${productId}">
          <div class="modal__inner">
            <div class="modal__centered">
              <div class="modal__centered-content">
                <div id="QuickShopHolder-${handle}"></div>
              </div>
              <button type="button" class="modal__close js-modal-close text-link">
                <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" viewBox="0 0 64 64">
                  <path d="m19 17.61 27.12 27.13m0-27.12L19 44.74"/>
                </svg>
                <span class="icon__fallback-text">Close (esc)</span>
              </button>
            </div>
          </div>
        </div>`;
      document.getElementById('modals-container').insertAdjacentHTML('beforeend', modalHTML);
      modal = document.querySelector(`#QuickShopModal-${productId}`);
    }
    return modal;
  }

  document.body.addEventListener('click', function (e) {
    const button = e.target.closest('.quick-product__icon-btn');
    if (!button) return;

    const gid = button.getAttribute('data-product-id');
    const productId = gid && gid.split('/').pop();
    const productHandle = button.getAttribute('data-product-handle');

    const modal = getOrCreateModal(productId, productHandle);
    modal.classList.add('modal--is-active');
    document.body.classList.add('modal-open');

    const targetDiv = modal.querySelector(`#QuickShopHolder-${productHandle}`);
    if (!targetDiv) return console.warn('Target div not found inside modal');

    targetDiv.innerHTML = '<div style="padding: 2rem; text-align: center;">Loading...</div>';

    fetch(`/products/${productHandle}`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch product page');
        return res.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const productSection =
          doc.querySelector(`#ProductSection-${productId}`) ||
          doc.querySelector('.product-single') ||
          doc.querySelector('.product-section') ||
          doc.querySelector('main');

        if (!productSection) {
          console.error('Product section not found in fetched HTML');
          targetDiv.innerHTML = "<div style='padding: 2rem; color: red;'>Quick view content not found.</div>";
          return;
        }

        targetDiv.innerHTML = productSection.outerHTML;

        productSection.querySelectorAll('script').forEach((oldScript) => {
          const newScript = document.createElement('script');
          if (oldScript.src) newScript.src = oldScript.src;
          else newScript.textContent = oldScript.textContent;
          document.body.appendChild(newScript);
        });
      })
      .catch((err) => {
        console.error('Failed to load quick view content:', err);
        targetDiv.innerHTML =
          '<div style="padding: 2rem; text-align: center; color: red;">Failed to load product details. Please try again.</div>';
      });
  });

  document.addEventListener('click', function (e) {
    if (e.target.closest('.js-modal-close')) {
      const modal = e.target.closest('.modal');
      if (modal) {
        modal.classList.remove('modal--is-active');
        document.body.classList.remove('modal-open');
      }
    }
  });

  /* ----- go ----- */
  resetAndLoad({ withFilters: true });
});